import { IKTRichText } from "@/types/kontent.types"
import {
  getChecklistSubscription,
  getChecklistSubscriptionFromItem,
} from "@/lib/kontent/components/getChecklistSubscription"
import { GetPub, GetPubFromItem } from "@/lib/kontent/components/getPub"
import {
  GetSimulateurChange,
  GetSimulateurChangeFromItem,
} from "@/lib/kontent/components/getSimulateurChange"
import { getWebSpotlightApplicationTree } from "@/lib/kontent/config/getApplicationTree"

import { GetButton, GetButtonFromItem } from "./components/getButton"
import {
  GetHighlightedContent,
  GetHighlightedContentFromItem,
} from "./components/getHighlightedContent"
import {
  GetTextBlocks,
  GetTextBlocksFromItem,
} from "./components/getTextBlocks"
import { GetTextMedia, GetTextMediaFromItem } from "./components/getTextMedia"
import {
  GetTextMediaGroup,
  GetTextMediaGroupFromItem,
} from "./components/getTextMediaGroup"
import { GetTextsList, GetTextsListFromItem } from "./components/getTextsList"

export async function resolveRichText(
  data: IKTRichText,
  preview = false
): Promise<IKTRichText> {
  let linkedItems: IKTRichText["linkedItems"] = undefined
  let itemHyperlinks: IKTRichText["itemHyperlinks"] = undefined
  try {
    if (data.linkedItems) {
      const richtextComponents = await Promise.all(
        data.linkedItems.items
          .filter(
            (item) =>
              item._system_.workflowStep._system_.codename !== "archived"
          )
          .map(async (item) => {
            try {
              const rawItem = item.rawItem
              switch (item.__typename) {
                case "TextsList":
                  return {
                    element: rawItem
                      ? await GetTextsListFromItem(rawItem)
                      : await GetTextsList(item._system_.codename, preview),
                    __typename: item.__typename,
                    _system_: item._system_,
                  }
                case "TextBlocks":
                  return {
                    element: rawItem
                      ? await GetTextBlocksFromItem(rawItem, preview)
                      : await GetTextBlocks(item._system_.codename, preview),
                    __typename: item.__typename,
                    _system_: item._system_,
                  }

                case "TextMediaGroup":
                  return {
                    element: rawItem
                      ? await GetTextMediaGroupFromItem(rawItem, preview)
                      : await GetTextMediaGroup(
                          item._system_.codename,
                          preview
                        ),
                    __typename: item.__typename,
                    _system_: item._system_,
                  }
                case "HighlightedContent":
                  return {
                    element: rawItem
                      ? await GetHighlightedContentFromItem(rawItem, preview)
                      : await GetHighlightedContent(
                          item._system_.codename,
                          preview
                        ),
                    __typename: item.__typename,
                    _system_: item._system_,
                  }
                case "Button":
                  return {
                    element: rawItem
                      ? await GetButtonFromItem(rawItem, preview)
                      : await GetButton(item._system_.codename, preview),
                    __typename: item.__typename,
                    _system_: item._system_,
                  }
                case "TextMedia":
                  return {
                    element: rawItem
                      ? await GetTextMediaFromItem(rawItem, preview)
                      : await GetTextMedia(item._system_.codename, preview),
                    __typename: item.__typename,
                    _system_: item._system_,
                  }
                case "Pub":
                  return {
                    element: rawItem
                      ? await GetPubFromItem(rawItem, preview)
                      : await GetPub(item._system_.codename, preview),
                    __typename: item.__typename,
                    _system_: item._system_,
                  }
                case "SimulateurChange":
                  return {
                    element: rawItem
                      ? await GetSimulateurChangeFromItem(rawItem)
                      : await GetSimulateurChange(
                          item._system_.codename,
                          preview
                        ),
                    __typename: item.__typename,
                    _system_: item._system_,
                  }
                case "ChecklistSubscription":
                  return {
                    element: rawItem
                      ? await getChecklistSubscriptionFromItem(rawItem, preview)
                      : await getChecklistSubscription(
                          item._system_.codename,
                          preview
                        ),
                    __typename: item.__typename,
                    _system_: item._system_,
                  }
                default:
                  return {
                    element: { data: "Not developed yet" },
                    __typename: item.__typename,
                    _system_: item._system_,
                  }
              }
            } catch (e) {
              return {
                element: { data: "error found" },
                __typename: "error",
                _system_: item._system_,
              }
            }
          })
      )
      linkedItems = { items: richtextComponents }
    }
  } catch (e) {
    console.error("Error linkedItems:", e)
    throw new Error("Error linkedItems.")
  }

  try {
    if (data.itemHyperlinks) {
      const { byCodename } = await getWebSpotlightApplicationTree(preview)

      let itemLinks = data.itemHyperlinks.items.map((item) => {
        return {
          _system_: {
            codename: item._system_.codename,
            id: item._system_.id,
            path: byCodename[item._system_.codename]?.path,
          },
        }
      })
      itemHyperlinks = { items: itemLinks }
    }
  } catch (e) {
    console.error("Error hyperlink:", e)
    throw new Error("Error hyperlink.")
  }

  return {
    html: data.html,
    assets: data.assets,
    linkedItems: linkedItems,
    itemHyperlinks: itemHyperlinks,
  }
}
